import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

interface GaugeProps {
  nutrient: string;
  actualValue: number;
  targetValue: number;
  isEmpty?: boolean;
  size?: number;
}

export function Gauge(props: GaugeProps) {
  const { t } = useTranslation();
  const { nutrient, actualValue, targetValue, isEmpty = false, size = 300 } = props;
  const safeActualValue = Number.isFinite(actualValue) ? actualValue : 0;
  const safeTargetValue = Number.isFinite(targetValue) ? targetValue : 0;
  const percentage = safeTargetValue > 0 ? Math.round((safeActualValue / safeTargetValue) * 100) : 0;
  const unit = nutrient === 'ENERGY' ? 'kcal' : 'g';

  const RADIAN = Math.PI / 180;
  const data = isEmpty
    ? [
      { name: 'under', value: 95, color: '#E8E1D9' },
      { name: 'optimum', value: 10, color: '#DAD2C9' },
      { name: 'over', value: 25, color: '#CFC6BC' }
    ]
    : [
      { name: 'under', value: 95, color: '#E9D38A' },
      { name: 'optimum', value: 10, color: '#93BFA0' },
      { name: 'over', value: 25, color: '#C08975' }
    ];

  const needle = (
    actual: number,
    target: number,
    cx: number,
    cy: number,
    iR: number,
    oR: number,
    color: string
  ) => {
    const safeActual = Number.isFinite(actual) ? actual : 0;
    const safeTarget = Number.isFinite(target) ? target : 0;
    const ratio = safeTarget > 0 ? safeActual / safeTarget : 0;
    const gaugeMaxRatio = 1.3;
    const clampedRatio = Math.min(Math.max(ratio, 0), gaugeMaxRatio);

    const ang = 180 * (1 - clampedRatio / gaugeMaxRatio);
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * ang);
    const cos = Math.cos(-RADIAN * ang);
    const r = 5; // radius of the needle base

    const x0 = cx;
    const y0 = cy;

    const xba = x0 + r * sin;
    const yba = y0 - r * cos;
    const xbb = x0 - r * sin;
    const ybb = y0 + r * cos;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;

    return [
      <circle key="needle-center" cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
      <path key="needle-path" d={`M${xba} ${yba}L${xbb} ${ybb}L${xp} ${yp}L${xba} ${yba}`} fill="#4C4743" />
    ];
  };

  const innerRadius = Math.round(size * 0.25);
  const outerRadius = Math.round(size * 0.4);
  const chartCenter = size / 2;

  return (
    <Box sx={{ textAlign: 'center', width: size, height: size }}>
      <Typography
        variant="overline"
        sx={{ mb: 0.5, letterSpacing: 1.5, color: '#6F675F', fontWeight: 600 }}
      >
        {t(`common.nutrient.${nutrient}`)}
      </Typography>

      <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700, color: '#2E2A26' }}>
        {percentage}%
      </Typography>

      <Typography variant="caption" sx={{ color: '#6F675F' }}>
        {safeActualValue} / {safeTargetValue} {unit}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', height: '100%' }}>
        <ResponsiveContainer width={size} height={size}>
          <PieChart>
            <Pie
              dataKey="value"
              startAngle={180}
              endAngle={0}
              data={data}
              cx={chartCenter}
              cy={chartCenter}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            {needle(
              safeActualValue,
              safeTargetValue,
              chartCenter,
              chartCenter,
              innerRadius,
              outerRadius,
              isEmpty ? '#B8B0A7' : '#7A4A2E'
            )}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
